import type { CoreConsentLegalDocumentResponse } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  CoreConsentLegalDocument,
  useGetCoreConsentLegalDocument,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import type { SWRResponse } from "swr"
import useSWR from "swr"
import { usePolyglot } from "src/contexts"

/*
 * we need this baseURL to request the tracking technology document
 *
 * the ApiContextProvider provides a baseURL to all api queries once the user
 * selects a country, but requesting tracking technology consent document comes
 * before that selection is made
 */
const baseURL: string = process.env.REACT_APP_SUGARMATE_BASE_URL ?? ""

export function useTrackingTechnologyConsentDocument(): SWRResponse<CoreConsentLegalDocumentResponse> {
  const polyglot = usePolyglot()
  const getLegalDocument = useGetCoreConsentLegalDocument(baseURL)

  const language = useMemo(
    // eslint-disable-next-line compat/compat
    () => new Intl.Locale(polyglot.locale()).language,
    [polyglot],
  )

  const swrKey = useMemo(
    () => {
      const url = `/api/v3/consent_tracking/${CoreConsentLegalDocument.CookieNotice}`

      return {
        url,
        params: {
          language,
        },
      }
    },
    [language],
  )

  const getTrackingTechnologyConsentDocument = useMemo(
    () => !getLegalDocument
      ? null
      : async () => {
        const response = await getLegalDocument(
          CoreConsentLegalDocument.CookieNotice,
          language,
        )
        return response.data
      },
    [
      language,
      getLegalDocument,
    ],
  )

  return useSWR<CoreConsentLegalDocumentResponse>(
    swrKey,
    getTrackingTechnologyConsentDocument,
  )
}
